import { Component, computed, inject, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { TranslationKey } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { StaticDataType } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { orderBy } from "lodash";
import { MultiSelect } from "primeng/multiselect";
import { Select } from "primeng/select";
import { Skeleton } from "primeng/skeleton";
import { map, startWith, switchMap } from "rxjs";
import { NewUiDropdownSortOnTopDirective } from "../../../shared/primeng/dropdown-sort-selected-value-on-top/new-ui-dropdown-sort-on-top.directive";
import { NewUiMultiSelectSortOnTopDirective } from "../../../shared/primeng/multi-select-sort-selected-values-on-top/new-ui-multi-select-sort-on-top.directive";
import { SettingsService } from "../../../shared/services/settings.service";
import { StaticDataService } from "../../../shared/static-data/static-data.service";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";
import { NewUiDashboardListComponent } from "../layout/new-ui-dashboard-list/new-ui-dashboard-list.component";
import { NewUiDashboardService } from "../new-ui-dashboard.service";

@Component({
  selector: "new-ui-dashboard-events",
  templateUrl: "./new-ui-dashboard-events.component.html",
  imports: [
    FormsModule,
    NewUiDashboardCardComponent,
    TranslateDirective,
    NewUiDashboardCardHeaderComponent,
    Select,
    NewUiDropdownSortOnTopDirective,
    NewUiDashboardListComponent,
    MultiSelect,
    NewUiMultiSelectSortOnTopDirective,
    EmptystateComponent,
    Skeleton,
  ],
})
export class NewUiDashboardEventsComponent {
  private readonly newUiDashboardService = inject(NewUiDashboardService);
  private readonly settings = inject(SettingsService);
  private readonly staticData = inject(StaticDataService);

  protected readonly isLicensed = this.settings.isLicensed;
  private readonly request = computed(() => ({
    organizationId: this.settings.organizationId,
    eventTimeframe: this.selectedTimeframe(),
    eventCategories: this.selectedEventTypes() ?? [],
  }));

  protected readonly eventCategories = toSignal(this.staticData.getStaticData(StaticDataType.CandidateEventCategory));
  protected readonly eventTypes = toSignal(this.staticData.getStaticData(StaticDataType.CandidateEvent));
  protected readonly documentTypes = toSignal(this.staticData.getStaticData(StaticDataType.AllowedUploadFileTypes));
  protected readonly timeframes = toSignal(
    this.staticData
      .getStaticData(StaticDataType.CandidateEventTimeframe)
      .pipe(map((entries) => orderBy(entries, (t) => t.value)))
  );

  protected readonly selectedTimeframe = signal<string>("007-DAYS");
  protected readonly selectedEventTypes = signal<string[]>([]);

  protected readonly candidateEventsMetric = toSignal(
    toObservable(this.request).pipe(
      switchMap((input) =>
        this.newUiDashboardService.getCandidateEventsMetric(input).pipe(
          map((res) => ({
            eventCounts: res.totalEvents ?? 0,
            events: res.events.map((event) => ({
              date: event.date,
              name: event.displayName,
              groupIcon: "icon-[mi--calendar-schedule-search-1]",
              group: this.eventTypes().find((et) => et.value === event.eventType)?.label,
              info:
                event.documentSetName || event.documentSetType
                  ? this.buildDocumentEventLabel(event.documentSetName, event.documentSetType, event.documentType)
                  : undefined,
              routeLink: this.settings.isLicensed ? "/app/candidates/events" : undefined,
              queryParams: {
                eventTimeframe: this.selectedTimeframe(),
                ...(this.selectedEventTypes().length > 0 ? { eventCategory: this.selectedEventTypes().join(",") } : {}),
              },
            })),
          })),
          startWith(null)
        )
      )
    )
  );

  private buildDocumentEventLabel(documentSetName: string, documentSetType: string, documentType: string): string {
    const documentSetKind =
      documentSetName ?? translateSafely(`documentSet.types.${documentSetType}` as TranslationKey);
    const documentTypeLabel = this.documentTypes().find((entry) => entry.value === documentType)?.label ?? "";
    return [documentTypeLabel, `(${documentSetKind})`].join(" ");
  }
}
